import React, { useEffect, useState, useContext, createContext } from "react";
import { usePreloadImages } from "../hooks/usePreloadImages";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { 
  getAuthToken, 
  getAdminProfileData, 
  setAdminProfileData, 
  setAdminName, 
  clearAllAuthCookies 
} from "../utils/cookieUtils";

const AdminProfileContext = createContext();

const AdminProfileProvider = ({ children }) => {
  const [adminProfileData, setAdminProfileDataState] = useState(
    getAdminProfileData() || null
  );
  const [roleId, setRoleId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminProfileData = async () => {
      const token = getAuthToken();

      if (!getAdminProfileData()) {
        try {
          const response = await axios.get(
            "/super-admin/get-admin-profile-data",
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setAdminProfileData(response.data);
          setAdminName(response.data.message.admin.f_name);
          setAdminProfileDataState(response.data);

          if (
            response.data &&
            response.data.message &&
            response.data.message.admin
          ) {
            setRoleId(response.data.message.admin.role.id);
          }
        } catch (error) {
          console.error("Error fetching admin profile data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        if (
          adminProfileData &&
          adminProfileData.message &&
          adminProfileData.message.admin
        ) {
          setRoleId(adminProfileData.message.admin.role.id);
        }
        setLoading(false);
      }
    };

    fetchAdminProfileData();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <div
          className="spinner"
          style={{
            width: 40,
            height: 40,
            border: "4px solid #eee",
            borderTop: "4px solid #007bff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        <p style={{ marginTop: 16, color: "#555" }}>Loading profile...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <AdminProfileContext.Provider value={{ adminProfileData, roleId }}>
      {children}
    </AdminProfileContext.Provider>
  );
};

function Sidebar() {
  const location = useLocation();
  const activeLink = location.pathname;

  const { adminProfileData, roleId } = useContext(AdminProfileContext);

  const handleSignOut = async () => {
    try {
      // Set natural logout flag to prevent session conflict detection
      sessionStorage.setItem('natural_logout', 'true');
      
      const token = getAuthToken();
      await axios.get("/super-admin/admin/set-last-seen", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      clearAllAuthCookies();
      window.location.href = "/login";
    } catch (error) {
      console.error("Error setting last seen:", error);
      clearAllAuthCookies();
      window.location.href = "/login";
    }
  };

  const images = [
    "frebson-logo.png",
    "admin-img.png",
    "icon-overview-active.png",
    "icon-overview-inactive.png",
    "icon-member-active.png",
    "icon-member-inactive.png",
    "icon-subscription-active.png",
    "icon-subscription-inactive.png",
    "icon-notifications-active.png",
    "icon-notifications-inactive.png",
    "icon-staff-active.png",
    "icon-staff-inactive.png",
    "icon-settings-active.png",
    "icon-settings-inactive.png",
    "icon-signout-active.png",
    "icon-signout-inactive.png",
    "action-log-active.png",
  ];
  usePreloadImages(images, `${process.env.PUBLIC_URL}/images`);

  const defaultProfileImage = `${process.env.PUBLIC_URL}/images/settings-avatar.png`;

  const getProfileImage = () => {
    const img = adminProfileData?.message?.admin?.profile_image;
    if (
      !img ||
      typeof img !== "string" ||
      img.trim() === "" ||
      img === "null" ||
      img === "undefined"
    ) {
      return defaultProfileImage;
    }
    return img;
  };

  return (
    <div className="sidebar">
      <div className="logo">
        <img
          src={`${process.env.PUBLIC_URL}/images/final-logo2.png`}
          alt="FrebsonLogo"
          aria-label="Frebson Logo"
        />
      </div>
      <div className="profile">
        <img
          src={getProfileImage()}
          alt="Profile"
          aria-label="Admin Profile Image"
          style={{ borderRadius: "50%" }}
        />
        <div className="profile-details">
          <p className="name">
            {adminProfileData.message.admin.f_name}
            {adminProfileData.message.admin.l_name !== null
              ? ` ${adminProfileData.message.admin.l_name}`
              : ""}
          </p>

          <p className="role">
            {adminProfileData.message.admin.role_id === 2
              ? "Admin"
              : adminProfileData.message.admin.role_id === 4
              ? "Branch Admin"
              : "Super Admin"}{" "}
            <span className="id">
              ( #{adminProfileData.message.admin.id} )
            </span>
          </p>
        </div>
      </div>
      <nav className="nav-menu">
        {activeLink === "/qr-code" ? (
          <span
            className="nav-item disabled"
            aria-label="QR Code Navigation Disabled"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icon-overview-inactive.png`}
              alt="QR Code"
              aria-label="QR Code Icon"
            />
            QR Code
          </span>
        ) : (
          <Link
            to="/dashboard"
            className={`nav-item ${
              activeLink === "/dashboard" ? "active" : ""
            }`}
            aria-label="Dashboard Navigation"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icon-overview-${
                activeLink === "/dashboard" ? "active" : "inactive"
              }.png`}
              alt="Overview"
              aria-label="Overview Icon"
            />
            Overview
          </Link>
        )}

        {activeLink !== "/qr-code" && (
          <>
            <Link
              to="/member-management"
              className={`nav-item ${
                activeLink === "/member-management" ? "active" : ""
              }`}
              aria-label="Member Management Navigation"
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/icon-member-${
                  activeLink === "/member-management" ? "active" : "inactive"
                }.png`}
                alt="Member Management"
                aria-label="Member Management Icon"
              />
              Member Management
            </Link>
            <Link
              to="/subscription"
              className={`nav-item ${
                activeLink === "/subscription" ? "active" : ""
              }`}
              aria-label="Subscription Navigation"
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/icon-subscription-${
                  activeLink === "/subscription" ? "active" : "inactive"
                }.png`}
                alt="Subscription"
                aria-label="Subscription Icon"
              />
              Subscription
            </Link>
            <Link
              to="/notifications"
              className={`nav-item ${
                activeLink === "/notifications" ? "active" : ""
              }`}
              aria-label="Notifications Navigation"
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/icon-notifications-${
                  activeLink === "/notifications" ? "active" : "inactive"
                }.png`}
                alt="Notifications"
                aria-label="Notifications Icon"
              />
              Notifications
            </Link>
            {roleId === 3 && (
              <Link
                to="/location"
                className={`nav-item ${
                  activeLink === "/location" ? "active" : ""
                }`}
                aria-label="Location Navigation"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/location-${
                    activeLink === "/location" ? "active" : "inactive"
                  }.png`}
                  alt="Location"
                  aria-label="Location Icon"
                />
                Location
              </Link>
            )}
            {(roleId === 3 || roleId === 4) && (
              <>
                <Link
                  to="/staff-management"
                  className={`nav-item ${
                    activeLink === "/staff-management" ? "active" : ""
                  }`}
                  aria-label="Staff Management Navigation"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icon-staff-${
                      activeLink === "/staff-management" ? "active" : "inactive"
                    }.png`}
                    alt="Staff Management"
                    aria-label="Staff Management Icon"
                  />
                  Staff Management
                </Link>
                {roleId === 3 && (
                  <Link
                    to="/action-log"
                    className={`nav-item ${
                      activeLink === "/action-log" ? "active" : ""
                    }`}
                    aria-label="Action Log Navigation"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/action-log-${
                        activeLink === "/action-log" ? "active" : "inactive"
                      }.png`}
                      alt="Action Log"
                      aria-label="Action Log Icon"
                    />
                    Action Log
                  </Link>
                )}
                <Link
                  to="/settings"
                  className={`nav-item ${
                    activeLink === "/settings" ? "active" : ""
                  }`}
                  aria-label="Settings Navigation"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icon-settings-${
                      activeLink === "/settings" ? "active" : "inactive"
                    }.png`}
                    alt="Settings"
                    aria-label="Settings Icon"
                  />
                  Settings
                </Link>
              </>
            )}
          </>
        )}

        <div
          className={`nav-item sign-out ${
            activeLink === "/sign-out" ? "active" : ""
          }`}
          onClick={handleSignOut}
          aria-label="Sign Out Navigation"
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/icon-signout-${
              activeLink === "/sign-out" ? "active" : "inactive"
            }.png`}
            alt="Sign Out"
            aria-label="Sign Out Icon"
          />
          Sign Out
        </div>
      </nav>
    </div>
  );
}

const App = () => {
  return (
    <AdminProfileProvider>
      <Sidebar />
    </AdminProfileProvider>
  );
};

export default App;
